import { useContext, useCallback } from "react";
import { SettingList } from "../mock-data/data";
import {
  Grid,
  Button,
  Typography,
  Box,
  Stack,
  Card,
  List,
  ListItem,
  ListItemText,
  Fade,
  Zoom,
  IconButton
} from "@mui/material";
import {
  GradienCheckCircleIcon,
  GradienCancelIcon,
} from "../component/icons/icon-gradient";

//component
import CardContainerMainLayout from "../component/card";
import DialogSettings from "../component/modals/dialog-settings";
//styles
import { CustomPaper, GradientTypography, BlackSpan } from "../styles/style";
import DataSourceContext from "../context/DataSourceMngContext";
import { SettingsIcon } from "lucide-react";

const SimpleBarChart = ({ success, failed }) => {
  const total = success + failed;
  const successPercentage = (success / total) * 100;
  const failedPercentage = (failed / total) * 100;

  return (
    <Box
      sx={{
        width: "100%",
        height: 10,
        borderRadius: 2,
        backgroundColor: "#e0e0e0",
      }}
    >
      <Box sx={{ display: "flex", height: "100%" }}>
        <Box
          sx={{
            width: `${successPercentage}%`,
            background: `linear-gradient(120deg, #3dbb89, #1b787a)`,
            borderRadius: failed ? "5px 0 0 5px" : "5px",
          }}
        />
        <Box
          sx={{
            width: `${failedPercentage}%`,
            background: `linear-gradient(120deg, #ef5350, #d32f2f)`,
            borderRadius: success ? "0 5px 5px 0" : "5px",
          }}
        />
      </Box>
    </Box>
  );
};

const CardDataSourceMng = () => {
  const {
    selectServer,
    servers,
    setDataState,
    fetchConfigure,
    Remove_Server,
    fetchDevice,
    dialog,
    settings,
  } = useContext(DataSourceContext);

  // const handleAddDataSource = useCallback(() => {
  //   setDialog('select_server', true);
  //   if(setup_servers?.length === 0){
  //     setLoading('modal', true);
  //     fetchServers();
  //   };
  // }, [setup_servers]);

  const handleServer = (server) => {
    // check if server is the same as the selected server (compare id or name)
    const isSameServer = selectServer?.id === server?.id && selectServer?.name === server?.name;
    
    if (isSameServer) {
      // if the card is the same, fetch new data
      setDataState((s) => ({
        ...s,
        loading: {
          ...s.loading,
          card: true,
        },
      }));
      // refresh data
      fetchDevice();
      return;
    }
    
    // if the card is new, clear devices and set loading
    setDataState((s) => ({
      ...s,
      selectServer: server,
      loading: {
        ...s.loading,
        card: true,
      },
      devices: {
        ...s.devices,
        results: [],
        count: 0,
      },
    }));
  };

  const ComfirmRemoveDataSource = () => {
    Remove_Server();
  };

  const handleEditDataSource = () => {
    setDataState((prevState) => ({
      ...prevState,
      selectServer: selectServer,
      dialog: {
        ...prevState.dialog,
        add_server: true,
      },
      loading: {
        ...prevState.loading,
        modal: true,
      },
      warning: {
        ...prevState.warning,
      },
    }));
    fetchConfigure();
  };

  const handleRemoveDataSource = () => {
    setDataState((s) => ({
      ...s,
      warning: {
        ...s.warning,
        open: true,
        severity: "warning",
        warningTitle: "Do you want to delete this data source.?",
        warningText: null,
        okBtnText: "Yes, Confirm",
        cancelBtnText: "Cancel",
        handleOkClicked: () => ComfirmRemoveDataSource(),
      },
    }));
  };

  const handleSettingsClick = () => {
    setDataState((prevState) => ({
      ...prevState,
      dialog: {
        ...prevState.dialog,
        settings: true,
      },
      settings: {
        ...prevState.settings,
        url: "https://ai-n8n-main.vekin.co.th", // Replace with your actual settings URL
      },
    }));
  };

  const handleCloseSettings = () => {
    setDataState((prevState) => ({
      ...prevState,
      dialog: {
        ...prevState.dialog,
        settings: false,
      },
    }));
  };

  const handleSetting = {
    edit: handleEditDataSource,
    delete: handleRemoveDataSource,
  };

  const SettingContents = () => {
    return (
      <List sx={{ padding: 0 }}>
        <ListItem sx={{ padding: 0 }}>Actions</ListItem>
        {SettingList?.map((s, i) => (
          <ListItem
            key={i}
            onClick={handleSetting[s.name]}
            sx={{ padding: 0, cursor: "pointer" }}
          >
            <ListItemText
              primary={
                <Box
                  sx={{
                    fontSize: "13px",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  {s.display_name} {s.icon}
                </Box>
              }
              sx={{
                backgroundImage: s.color,
                borderRadius: "7px",
                padding: "5px 10px",
                color: "white",
              }}
            />
          </ListItem>
        ))}
      </List>
    );
  };


  return (
    <CustomPaper>
      <Stack spacing={2}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Fade in={true}>
            <Typography variant="h3">Data Source</Typography>
          </Fade>

          <IconButton
            onClick={handleSettingsClick}
          >
            <SettingsIcon/>
          </IconButton>
          {/* <Button variant="contained" size="small" sx={{ width: '130px', height: '38px' }} onClick={handleAddDataSource}>
            Add Data Source 
          </Button> */}
        </Box>
        <Box sx={{ height: "100%", overflowX: "auto", overflowY: "hidden", paddingBottom: 1 }}>
          <Box sx={{ display: "flex", gap: 2, padding: 1, minWidth: "max-content"}}>
            {servers?.length > 0 ? (
              servers?.map((s, index) => (
                <Zoom key={index} in={true} timeout={500 * index}>
                  <Box 
                    sx={{ width: "280px", cursor: "pointer" }}
                    onClick={() => handleServer(s)}
                  >
                    <CardContainerMainLayout
                      popup={<SettingContents />}
                      title={s.name || "..."}
                      transform={!selectServer?.name}
                      isBorder={s.name === selectServer?.name}
                      serverData={s}
                      selectedServer={selectServer}
                    >
                      <Box sx={{ display: "flex" , alignItems: "center", gap: 0.5 }}>
                        <GradientTypography
                          variant="h4"
                          sx={{ textShadow: "2px 2px 4px rgba(0, 0, 0, 0.2)" }}
                        >
                          {s.total}{" "}
                          <BlackSpan sx={{ fontSize: "14px" }}>Files</BlackSpan>
                        </GradientTypography>
                      </Box>
                      <SimpleBarChart success={s.success} failed={s.failed} />
                      <Box sx={{ marginTop: "10px" }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <GradienCheckCircleIcon fontSize="25px" />
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: "bold",
                                color: "gray",
                                marginLeft: "5px",
                              }}
                            >
                              Success
                            </Typography>
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: "bold",
                              color: "success.main",
                              textShadow: "2px 2px 4px rgba(0, 0, 0, 0.2)",
                            }}
                          >
                            {s.success}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <GradienCancelIcon fontSize="25px" />
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: "bold",
                                color: "gray",
                                marginLeft: "5px",
                              }}
                            >
                              Failed
                            </Typography>
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: "bold",
                              color: "error.main",
                              textShadow: "2px 2px 4px rgba(0, 0, 0, 0.2)",
                            }}
                          >
                            {s.failed}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContainerMainLayout>
                  </Box>
                </Zoom>
              ))
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", minHeight: "200px" }} >
                <Card
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "2%",
                    boxShadow: "none",
                    border: "1px solid #e0e0e0",
                    borderRadius: "12px",
                    color: "gray",
                    fontSize: "14px",
                    minWidth: "200px"
                  }}
                >
                  No data source
                </Card>
              </Box>
            )}
          </Box>
        </Box>
      </Stack>
      
      <DialogSettings
        open={dialog.settings}
        onClose={handleCloseSettings}
        url={settings.url}
      />
    </CustomPaper>
  );
};

export default CardDataSourceMng;
